// Clientes Facturas - Facturas emitidas (descargables) + pagos/cuotas desde ficha del cliente
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/common/EmptyState';
import {
  DocumentDuplicateIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useClienteFichaQuery } from '@/hooks/useClienteExpediente';
import { useClienteFacturasQuery } from '@/hooks/useClienteFacturas';
import { clienteService } from '@/services/clienteService';
import type { ClienteFacturaItem, ClienteFacturaStatus } from '@/types/clienteFactura';

const STATUS_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  paid: { label: 'Pagada', variant: 'default' },
  succeeded: { label: 'Pagada', variant: 'default' },
  open: { label: 'Pendiente', variant: 'secondary' },
  pending: { label: 'Pendiente', variant: 'secondary' },
  overdue: { label: 'Vencida', variant: 'destructive' },
  failed: { label: 'Fallida', variant: 'destructive' },
  void: { label: 'Anulada', variant: 'outline' },
  refunded: { label: 'Reembolsada', variant: 'outline' },
};

// Badges de estado de facturas emitidas (contrato /clientes/facturas) — i18n, NO reutiliza el legacy
const FACTURA_STATUS_BADGE: Record<
  ClienteFacturaStatus,
  { labelKey: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  open: { labelKey: 'invoices.statusOpen', variant: 'secondary' },
  paid: { labelKey: 'invoices.statusPaid', variant: 'default' },
  void: { labelKey: 'invoices.statusVoid', variant: 'outline' },
  uncollectible: { labelKey: 'invoices.statusUncollectible', variant: 'destructive' },
};

function formatCurrency(amountCents: number, currency: string): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function ClientesFacturas() {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();
  const { data: ficha, isLoading, error: queryError } = useClienteFichaQuery();
  const {
    data: facturasData,
    isLoading: facturasLoading,
    error: facturasError,
    refetch: refetchFacturas,
  } = useClienteFacturasQuery();
  const [filter, setFilter] = useState<'all' | 'paid' | 'pending'>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadErrorId, setDownloadErrorId] = useState<string | null>(null);

  const facturas = facturasData?.items ?? [];

  const payments = useMemo(() => {
    const items = ficha?.billing_payments ?? [];
    const sorted = [...items].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    if (filter === 'all') return sorted;
    if (filter === 'paid') return sorted.filter(p => p.status === 'paid' || p.status === 'succeeded');
    return sorted.filter(p => p.status !== 'paid' && p.status !== 'succeeded');
  }, [ficha?.billing_payments, filter]);

  const handleDownload = async (factura: ClienteFacturaItem) => {
    setDownloadingId(factura.id);
    setDownloadErrorId(null);
    try {
      await clienteService.downloadFacturaPdf(factura.id, factura.invoice_number);
    } catch {
      setDownloadErrorId(factura.id);
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="space-y-6">
          <div>
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-2" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </ClientLayout>
    );
  }

  if (queryError) {
    return (
      <ClientLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('invoices.title')}</h1>
          </div>
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <p className="text-red-600 dark:text-red-400 mb-4">{t('invoices.loadError', 'Error al cargar tus facturas.')}</p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                {t('common.retry', 'Reintentar')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('invoices.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('invoices.subtitle')}
          </p>
        </div>

        {/* ── Facturas emitidas (descargables) ── */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('invoices.sectionInvoicesTitle', 'Facturas emitidas')}
          </h2>

          {facturasLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : facturasError ? (
            <Card>
              <CardContent className="pt-6 text-center py-8">
                <p className="text-red-600 dark:text-red-400 mb-4">
                  {t('invoices.loadError', 'No se pudieron cargar tus facturas')}
                </p>
                <Button variant="outline" onClick={() => refetchFacturas()}>
                  {t('invoices.retry', 'Reintentar')}
                </Button>
              </CardContent>
            </Card>
          ) : facturas.length > 0 ? (
            <Card>
              <CardContent className="pt-6 space-y-3">
                {facturas.map((factura) => {
                  const statusInfo = FACTURA_STATUS_BADGE[factura.status] ?? {
                    labelKey: '',
                    variant: 'secondary' as const,
                  };
                  const isDownloading = downloadingId === factura.id;
                  return (
                    <div
                      key={factura.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {factura.invoice_number}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {formatDate(factura.created_at)}
                          {factura.due_date && (
                            <> · {t('invoices.dueDate', { date: formatDate(factura.due_date) })}</>
                          )}
                        </p>
                        {downloadErrorId === factura.id && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            {t('invoices.downloadError', 'No se pudo descargar la factura. Inténtalo de nuevo.')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 sm:ml-4">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(Math.round(factura.total * 100), factura.currency)}
                        </span>
                        <Badge variant={statusInfo.variant}>
                          {statusInfo.labelKey ? t(statusInfo.labelKey) : factura.status}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!factura.pdf_available || isDownloading}
                          title={!factura.pdf_available ? t('invoices.pdfNotAvailable', 'PDF no disponible') : undefined}
                          onClick={() => handleDownload(factura)}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                          {isDownloading
                            ? t('invoices.downloading', 'Descargando...')
                            : t('invoices.downloadPdf', 'Descargar PDF')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <EmptyState
              icon={<DocumentTextIcon width={48} height={48} className="text-gray-400" />}
              title={t('invoices.noIssuedInvoices', 'Aún no tienes facturas emitidas')}
              description={t('invoices.noIssuedInvoicesDesc')}
            />
          )}
        </section>

        {/* ── Pagos y cuotas (Stripe) ── */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('invoices.sectionPaymentsTitle', 'Pagos y cuotas')}
          </h2>

          {/* Info Stripe */}
          <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <DocumentDuplicateIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <p className="font-medium mb-1">{t('invoices.stripeInfoTitle')}</p>
                  <p className="text-blue-700 dark:text-blue-300">{t('invoices.stripeInfoDesc', 'Puedes gestionar tus facturas y métodos de pago desde el portal de Stripe en la sección Suscripciones.')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filtros */}
          {payments.length > 0 && (
            <div className="flex gap-2" role="group" aria-label={t('invoices.filterLabel', 'Filtrar facturas')}>
              {(['all', 'paid', 'pending'] as const).map((f) => (
                <Button
                  key={f}
                  variant={filter === f ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? t('invoices.filterAll', 'Todas') : f === 'paid' ? t('invoices.filterPaid', 'Pagadas') : t('invoices.filterPending', 'Pendientes')}
                </Button>
              ))}
            </div>
          )}

          {/* Lista de pagos */}
          {payments.length > 0 ? (
            <Card>
              <CardContent className="pt-6 space-y-3">
                {payments.map((payment) => {
                  const statusInfo = STATUS_BADGE[payment.status] || { label: payment.status, variant: 'secondary' as const };
                  return (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {payment.description || t('invoices.paymentGeneric', 'Pago')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {formatDate(payment.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(payment.amount_cents, payment.currency)}
                        </span>
                        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <EmptyState
              icon={<DocumentDuplicateIcon width={48} height={48} className="text-gray-400" />}
              title={t('invoices.noInvoices')}
              description={t('invoices.noInvoicesDesc')}
              action={
                <Button
                  onClick={() => navigate('/clientes/suscripciones')}
                  variant="outline"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                  {t('invoices.goToSubscriptions')}
                </Button>
              }
            />
          )}
        </section>
      </div>
    </ClientLayout>
  );
}
